import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, FormControlLabel, FormGroup, Switch } from "@mui/material";
import { uniqBy } from "lodash-es";
import { useTranslation } from "react-i18next";
import { firstBy } from "thenby";

import { dayjs } from "@thalamos/common";
import type {
  FormContextData,
  FormContextMiniForm,
  FormPartTemplate,
  PatientTimelineResponse,
  SelectedFormsObject,
  ValidationOutcome,
} from "@thalamos/aspire-common";
import {
  findFormsMerged,
  formIsCollapsedRedoReplacement,
  formIsCompleted,
  formIsValidForProgression,
  formIsVoid,
} from "@thalamos/aspire-common";

import { FormContextPdfViewer } from "../FormProgressPage/helpers/FormContextPdfViewer.js";
import { FormCheckboxRow } from "./AdmissionFormCheckboxRow.js";
import {
  Banner,
  BannerList,
  Dropdown,
} from "~/components/design-system/index.js";

export type DialogType = "record-of-detention" | undefined;

// An application must be made within 14 days of the last medical examination,
// and a form is signed no earlier than that examination, so a med rec signed
// longer ago than this can never be a valid basis.
function signedWithinWindow(form: FormContextMiniForm): boolean {
  return (
    form.versions[form.latestVersion - 1]?.signatures.some((s) =>
      dayjs(s.legalSignatureDate).isAfter(
        dayjs.utc().subtract(2, "weeks").startOf("day"),
      ),
    ) ?? false
  );
}

export function FormSupportingDocumentsSelector({
  linkableForms,
  formContext,
  setValidationOutcome,
  setSupportingFormsProvided,
  setSelectedForms,
  selectedForms,
  setNotSelectedReason,
  supportingFormsProvided,
  notSelectedReason,
  dialogType,
  patientTimeline,
}: {
  linkableForms: FormPartTemplate<object>["signing"]["linkableForms"];
  formContext: Omit<FormContextData, "patient">;
  supportingFormsProvided: boolean;
  setSupportingFormsProvided: (value: boolean) => void;
  selectedForms: SelectedFormsObject[];
  setSelectedForms: (value: SelectedFormsObject[]) => void;
  notSelectedReason: string;
  setNotSelectedReason: (value: string) => void;
  setValidationOutcome: (validationOutcome: ValidationOutcome[]) => void;
  dialogType?: DialogType;
  patientTimeline: PatientTimelineResponse;
}) {
  const { t } = useTranslation();

  const [pdfViewFormId, setPdfViewFormId] = useState<null | string>(null);

  // The candidate set is the templates this form can link to, minus deleted
  // and voided forms: neither exists as far as the author is concerned, a
  // voided one having been superseded. A form that survives is listed even
  // when it cannot be used, so the author can see what is blocking them.
  const isCandidateBasis = useCallback(
    (form: FormContextMiniForm) =>
      form.status !== "deleted" &&
      !formIsVoid(form) &&
      linkableForms !== undefined &&
      linkableForms.formTemplateIds.includes(form.formTemplate.id),
    [linkableForms],
  );

  // Memoised for identity stability, not cost: validationOutcome derives from
  // this list and is pushed into the parent's state from an effect, so a fresh
  // array every render would re-render forever.
  const patientForms = useMemo(() => {
    const forms = uniqBy(
      (patientTimeline.inProgressWork.admissions ?? []).flatMap(
        (fc) => fc.forms,
      ),
      "id",
    );

    return (
      forms
        .filter(isCandidateBasis)
        .filter((f) => !formIsCollapsedRedoReplacement(f, forms))
        // A valid basis is offered only while recently signed: the source spans
        // every open admission, so old signed forms would otherwise pile up.
        // One that is not a valid basis is listed regardless of age, so the
        // author sees the whole in-flight set.
        .filter((f) => !formIsValidForProgression(f) || signedWithinWindow(f))
    );
  }, [patientTimeline, isCandidateBasis]);

  const relevantForms = useMemo(() => {
    const forms =
      linkableForms?.source === "form-context"
        ? formContext.forms
            .filter(isCandidateBasis)
            // A record of detention's basis must already be complete: a redo
            // can leave an unsigned replacement in the admission, and offering
            // it would show an author-less row nobody can select.
            .filter(
              (f) => dialogType !== "record-of-detention" || formIsCompleted(f),
            )
        : patientForms;

    return (forms ?? []).sort(firstBy<FormContextMiniForm>("updated", -1));
  }, [
    patientForms,
    formContext.forms,
    linkableForms?.source,
    isCandidateBasis,
    dialogType,
  ]);

  const validationOutcome = useMemo(
    () =>
      linkableForms?.checkValidityFn(
        relevantForms.filter((f) =>
          selectedForms.some((s) => s.id.includes(f.id)),
        ) ?? [],
      ) ?? [],
    [linkableForms, relevantForms, selectedForms],
  );

  // If there is only one selectable form to pick and only one form is required
  // then keep it selected. The ref tracks a genuine user *deselection* — once
  // the user unticks the form we never re-select it automatically; until then
  // the effect is idempotent and re-selects after any external reset (e.g. the
  // useClearSupportingDocuments reset that fires on the same commit the dialog
  // first mounts a template, which would otherwise wipe the auto-selection).
  const userDeselectedRef = useRef(false);
  useEffect(() => {
    const selectable = relevantForms.filter(formIsValidForProgression);
    const onlySelectableForm =
      selectable.length === 1 ? selectable[0] : undefined;
    if (
      !userDeselectedRef.current &&
      onlySelectableForm !== undefined &&
      selectedForms.length === 0 &&
      !linkableForms?.isMultiSelect
    ) {
      setSelectedForms([
        {
          id: onlySelectableForm.id,
          templateId: onlySelectableForm.formTemplate.id,
        },
      ]);
    }
  }, [
    relevantForms,
    selectedForms,
    setSelectedForms,
    linkableForms?.isMultiSelect,
  ]);

  // When bypass is toggled ON, we intentionally preserve selectedForms
  // so users can link some med recs digitally while marking others as
  // completed on paper ("partially-provided" scenario).

  useEffect(() => {
    setValidationOutcome(validationOutcome);
  }, [setValidationOutcome, selectedForms, validationOutcome]);

  const bodyValidationOutcome = (outcome: ValidationOutcome) => {
    const reason = outcome.reason;
    if (!reason) return "";

    const isOutcomeSuccessOrError =
      outcome.type === "success" || outcome.type === "error";

    if (typeof reason === "string" && isOutcomeSuccessOrError) {
      return [reason];
    }

    if (isOutcomeSuccessOrError) return reason;
  };

  const isBypassable = linkableForms?.isBypassable;

  // When the bypass toggle ("completed outside eMHA") is on we still surface
  // error-level outcomes — an invalid combination (e.g. two med recs by the same
  // doctor) must never be hidden. Success/warning banners stay hidden while the
  // toggle is on, as the user is intentionally not providing a full selection.
  const visibleValidationOutcomes = supportingFormsProvided
    ? validationOutcome.filter((outcome) => outcome.type === "error")
    : validationOutcome;

  const relevantMergedForms = relevantForms
    .filter((form) =>
      findFormsMerged(patientTimeline?.mergedPatientData, formContext, form),
    )
    .filter((f) => selectedForms.find((s) => s.id === f.id));

  return (
    <Box data-testid="supporting-documents-selector">
      {!!relevantMergedForms.length && (
        <Box sx={{ mb: 2 }}>
          <Banner
            title={t(
              "pages.formDraftSignPage.supportingDocuments.patientMergeWarningMessage",
            )}
            bannerType={BannerList.WARNING}
          />
        </Box>
      )}
      {visibleValidationOutcomes.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <FormGroup>
            {visibleValidationOutcomes.map((validationOutcome, index) => (
              <Banner
                key={`${validationOutcome.type}:${String(index)}`}
                sx={{ mb: 2 }}
                bannerType={
                  validationOutcome.type === "warning"
                    ? BannerList.WARNING
                    : validationOutcome.type === "error"
                      ? BannerList.ERROR
                      : BannerList.CONFIRMATION
                }
                title={
                  validationOutcome.type === "success"
                    ? t(
                        "pages.formDraftSignPage.supportingDocuments.validationSuccess.messageTitle",
                      )
                    : validationOutcome.type === "error"
                      ? t(
                          "pages.formDraftSignPage.supportingDocuments.validationError.messageTitle",
                        )
                      : [validationOutcome.reason].flat(2).join(" ")
                }
                body={bodyValidationOutcome(validationOutcome)}
                info={
                  validationOutcome.type === "success"
                    ? t(
                        "pages.formDraftSignPage.supportingDocuments.validationSuccess.messageInfo",
                      )
                    : undefined
                }
              />
            ))}
          </FormGroup>
        </Box>
      )}
      {isBypassable && (
        <Box sx={{ my: 2 }}>
          <FormControlLabel
            control={
              <Switch
                sx={{
                  "& .MuiSwitch-track": {
                    backgroundColor: "gray",
                  },
                }}
                checked={supportingFormsProvided}
                onClick={() => {
                  setSupportingFormsProvided(!supportingFormsProvided);
                  setNotSelectedReason("");
                }}
              />
            }
            label={t("pages.formDraftSignPage.medicalRecommendationsLabel")}
          />
        </Box>
      )}
      {supportingFormsProvided && isBypassable && (
        <Box sx={{ mb: 2 }}>
          <Dropdown
            selectedValue={notSelectedReason}
            name={"not-provided-reason"}
            values={[
              { value: "completedOnPaper", label: "Completed on paper" },
              { value: "other", label: "Other" },
            ]}
            onChange={(e) => {
              setNotSelectedReason(e);
            }}
          />
        </Box>
      )}
      {relevantForms &&
        relevantForms.map((f) => {
          const checked = selectedForms.some((s) => s.id.includes(f.id));

          const isMultiSelect = linkableForms?.isMultiSelect;

          const shouldSingleSelectCheckBox = supportingFormsProvided;

          const merged = findFormsMerged(
            patientTimeline.mergedPatientData,
            formContext,
            f,
          );

          return (
            <FormCheckboxRow
              key={f.id}
              setSelectedForms={setSelectedForms}
              setPdfViewFormId={setPdfViewFormId}
              form={f}
              formContext={formContext}
              checked={checked}
              // Only a form valid as a basis can be selected; others remain
              // visible for context but cannot be chosen.
              selectable={formIsValidForProgression(f)}
              isMultiSelect={isMultiSelect ?? false}
              isSingleSelectCheckBox={shouldSingleSelectCheckBox}
              lastMerged={merged?.lastMerged}
              onUserDeselect={() => {
                userDeselectedRef.current = true;
              }}
            />
          );
        })}
      {relevantForms && pdfViewFormId && (
        <FormContextPdfViewer
          inModal={true}
          // Filtered before sorting: relevantForms is memoised, and sort
          // mutates, so it must not be sorted in place.
          forms={relevantForms
            .filter((f) => f.status !== "deleted")
            .sort((f1, f2) => (f1.updated < f2.updated ? 1 : -1))}
          setFormId={setPdfViewFormId}
          formId={pdfViewFormId}
        />
      )}
    </Box>
  );
}
